import React from "react";
import { Badge, Box, CheckboxCard, Grid, HStack, Icon, Stack, Text, Tooltip, createListCollection } from "@chakra-ui/react";
import { MdPeople, MdPerson, MdAccessTime, MdLogout } from "react-icons/md";
import { AvatarSelectField } from "components/utils/AvatarSelectField";
import { DashboardCheckboxCardControl, DashboardTruncatedText } from "components/utils/FormControls";
import AppAvatar from "components/utils/AppAvatar";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { TagCombobox } from "components/utils/TagCombobox";
import { CompactEmojiRatingPicker } from "../components/CompactEmojiRatingPicker";
import { emojiForRating, labelForRating } from "../components/EmojiRatingBar";
import { useDialogForm } from "utils/useDialogForm";
import { getAvatarHexColor, getAvatarTint, resolveAvatarUrl } from "utils/avatar";
import { asArray, isPrefixedTempId, makePrefixedTempId, normalizeRosterRows, toAvatarSelectItems, toNullableId, toNullableInteger } from "../shared/helpers";
import {
  useAddDashboardActividadAsistenciaInstructor,
  useAddDashboardActividadAsistenciaSocio,
  useCreateDashboardActividadRemplazo,
  useDashboardActividadAsistenciaInstructores,
  useDashboardActividadAsistenciaSocios,
  useDashboardActividadRemplazos,
  useDashboardActividadRosters,
  useDeleteDashboardActividadRemplazo,
  usePatchDashboardActividadAsistenciaInstructor,
  usePatchDashboardActividadRemplazo,
  useRemoveDashboardActividadAsistenciaInstructor,
  useRemoveDashboardActividadAsistenciaSocio,
} from "features/dashboard/actividades/hooks";

const TMP_PREFIX = "tmp_";

function puntualidadToFlags(v) {
  if (v === 3) return { llegoTarde: true, seFueTemprano: true };
  if (v === 1) return { llegoTarde: true, seFueTemprano: false };
  if (v === 2) return { llegoTarde: false, seFueTemprano: true };
  return { llegoTarde: false, seFueTemprano: false };
}

function flagsToPuntualidad(llegoTarde, seFueTemprano) {
  if (llegoTarde && seFueTemprano) return 3;
  if (llegoTarde) return 1;
  if (seFueTemprano) return 2;
  return 0;
}

function rowTone(seed) {
  const solid = getAvatarHexColor(seed);
  return { solid, bg: getAvatarTint(seed, 12) };
}

function hasRating(value) {
  return value != null && value !== "" && Number.isFinite(Number(value));
}

function PunctualityAndRating({ puntualidad, calificacion }) {
  const flags = puntualidadToFlags(Number(puntualidad));
  const showRating = hasRating(calificacion);

  if (!flags.llegoTarde && !flags.seFueTemprano && !showRating) return null;

  return (
    <HStack gap="2" align="center" flexShrink={0}>
      {showRating ? (
        <Tooltip.Root openDelay={0}>
          <Tooltip.Trigger asChild>
            <Text fontSize="lg" lineHeight="1" cursor="default">{emojiForRating(calificacion)}</Text>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>{labelForRating(calificacion)}</Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      ) : null}

      {flags.llegoTarde ? (
        <Tooltip.Root openDelay={0}>
          <Tooltip.Trigger asChild>
            <Icon color="orange.400" fontSize="lg" cursor="default">
              <MdAccessTime />
            </Icon>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>Llego Tarde</Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      ) : null}

      {flags.seFueTemprano ? (
        <Tooltip.Root openDelay={0}>
          <Tooltip.Trigger asChild>
            <Icon color="blue.400" fontSize="lg" cursor="default">
              <MdLogout />
            </Icon>
          </Tooltip.Trigger>
          <Tooltip.Positioner>
            <Tooltip.Content>Se fue Temprano</Tooltip.Content>
          </Tooltip.Positioner>
        </Tooltip.Root>
      ) : null}

    </HStack>
  );
}


function HeaderRow({ c1, c2, c3 }) {
  return (
    <Grid templateColumns="2fr 2fr 1.5fr" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
      <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">{c1}</Text>
      <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted" textAlign="center">{c2}</Text>
      <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted" textAlign="center">{c3}</Text>
    </Grid>
  );
}

export const PosteriorAsistenciaCard = ({ actividadId }) => {
  const dialog = useDialogForm({ socioIds: [], instructores: [], remplazos: [] });
  const [localError, setLocalError] = React.useState(null);

  const rostersQ = useDashboardActividadRosters(true);
  const sociosQ = useDashboardActividadAsistenciaSocios(actividadId, !!actividadId);
  const instructoresQ = useDashboardActividadAsistenciaInstructores(actividadId, !!actividadId);
  const remplazosQ = useDashboardActividadRemplazos(actividadId, !!actividadId);

  const addSocio = useAddDashboardActividadAsistenciaSocio();
  const removeSocio = useRemoveDashboardActividadAsistenciaSocio();
  const addInstructor = useAddDashboardActividadAsistenciaInstructor();
  const patchInstructor = usePatchDashboardActividadAsistenciaInstructor();
  const removeInstructor = useRemoveDashboardActividadAsistenciaInstructor();
  const createRemplazo = useCreateDashboardActividadRemplazo();
  const patchRemplazo = usePatchDashboardActividadRemplazo();
  const deleteRemplazo = useDeleteDashboardActividadRemplazo();

  const saving = !!(
    addSocio.isPending ||
    removeSocio.isPending ||
    addInstructor.isPending ||
    patchInstructor.isPending ||
    removeInstructor.isPending ||
    createRemplazo.isPending ||
    patchRemplazo.isPending ||
    deleteRemplazo.isPending
  );

  const apiError =
    (addSocio.isError && String(addSocio.error?.message || addSocio.error)) ||
    (removeSocio.isError && String(removeSocio.error?.message || removeSocio.error)) ||
    (addInstructor.isError && String(addInstructor.error?.message || addInstructor.error)) ||
    (patchInstructor.isError && String(patchInstructor.error?.message || patchInstructor.error)) ||
    (removeInstructor.isError && String(removeInstructor.error?.message || removeInstructor.error)) ||
    (createRemplazo.isError && String(createRemplazo.error?.message || createRemplazo.error)) ||
    (patchRemplazo.isError && String(patchRemplazo.error?.message || patchRemplazo.error)) ||
    (deleteRemplazo.isError && String(deleteRemplazo.error?.message || deleteRemplazo.error)) ||
    null;

  const errorText = localError || apiError;

  const sociosGrupo = asArray(rostersQ.data?.sociosGrupo);
  const sociosPeriodo = asArray(rostersQ.data?.sociosPeriodo);
  const sociosDisponibles = sociosGrupo.length ? sociosGrupo : sociosPeriodo;
  const instructoresPeriodo = React.useMemo(
    () =>
      normalizeRosterRows(rostersQ.data?.instructoresPeriodo, {
        idKeys: ["id", "instructorId"],
        nameKeys: ["nombre", "instructorNombre"],
        fallbackName: "Instructor",
      }),
    [rostersQ.data?.instructoresPeriodo]
  );
  const remplazosSemestre = React.useMemo(
    () =>
      normalizeRosterRows(rostersQ.data?.remplazosSemestre, {
        idKeys: ["id", "instructorId"],
        nameKeys: ["nombre", "instructorNombre"],
        fallbackName: "Instructor",
      }),
    [rostersQ.data?.remplazosSemestre]
  );

  const sociosAsistenciaPayload = React.useMemo(() => sociosQ.data ?? {}, [sociosQ.data]);
  const asistenciaSocios = React.useMemo(() => {
    if (Array.isArray(sociosAsistenciaPayload)) return sociosAsistenciaPayload;
    return asArray(sociosAsistenciaPayload?.rows);
  }, [sociosAsistenciaPayload]);
  const asistenciaInstructores = asArray(instructoresQ.data);
  const asistenciaRemplazos = asArray(remplazosQ.data);

  const sociosItems = React.useMemo(
    () => sociosDisponibles.map((s) => ({ label: s.nombre || "Socio", value: s.id })).filter((x) => x.value),
    [sociosDisponibles]
  );
  const instructoresItems = React.useMemo(
    () => toAvatarSelectItems(instructoresPeriodo, { fallbackLabel: "Instructor" }),
    [instructoresPeriodo]
  );
  const remplazosItems = React.useMemo(
    () => toAvatarSelectItems(remplazosSemestre, { fallbackLabel: "Instructor" }),
    [remplazosSemestre]
  );

  const instructoresCollection = React.useMemo(() => createListCollection({ items: instructoresItems }), [instructoresItems]);
  const solicitanteCollection = instructoresCollection;

  const nombreInstructorById = React.useMemo(
    () => new Map([...instructoresItems, ...remplazosItems].map((x) => [x.value, x.label])),
    [instructoresItems, remplazosItems]
  );
  const instructoresById = React.useMemo(
    () => new Map(instructoresPeriodo.map((i) => [String(i.id), i])),
    [instructoresPeriodo]
  );
  const remplazosById = React.useMemo(
    () => new Map(remplazosSemestre.map((i) => [String(i.id), i])),
    [remplazosSemestre]
  );

  const sociosRegistrados = Number(
    sociosAsistenciaPayload?.resumen?.sociosRegistrados ??
      (asistenciaSocios.length ? asistenciaSocios.length : 0)
  );
  const sociosNoRegistrados = Number(
    sociosAsistenciaPayload?.resumen?.sociosNoRegistrados ??
      Math.max(0, sociosItems.length - asistenciaSocios.length)
  );
  const isIncomplete = React.useMemo(
    () =>
      asistenciaSocios.length === 0 ||
      asistenciaInstructores.length === 0 ||
      asistenciaInstructores.some((row) => !hasRating(row.calificacion)) ||
      asistenciaRemplazos.some((row) => !row?.instructorId || !row?.remplazoId || !hasRating(row.calificacion)),
    [asistenciaInstructores, asistenciaRemplazos, asistenciaSocios.length]
  );

  function openDialog() {
    setLocalError(null);
    dialog.openWith({
      socioIds: asistenciaSocios.map((x) => x.socioId || x.id).filter(Boolean),
      instructores: asistenciaInstructores.map((x) => ({
        id: `ins_${x.instructorId}`,
        instructorId: x.instructorId,
        ...puntualidadToFlags(Number(x.puntualidad)),
        calificacion: x.calificacion == null ? "" : String(x.calificacion),
      })),
      remplazos: asistenciaRemplazos.map((x) => ({
        id: x.id,
        instructorId: x.instructorId ?? "",
        remplazoId: x.remplazoId ?? "",
        calificacion: x.calificacion == null ? "" : String(x.calificacion),
      })),
    });
  }

  function setSelectedInstructores(ids) {
    const idSet = new Set((ids || []).map(String));
    const prevByInstructor = new Map(
      asArray(dialog.form.instructores).map((r) => [String(r.instructorId), r])
    );

    const nextRows = [];
    for (const instructorId of idSet) {
      const prev = prevByInstructor.get(instructorId);
      nextRows.push(
        prev ?? {
          id: `ins_${instructorId}`,
          instructorId,
          llegoTarde: false,
          seFueTemprano: false,
          calificacion: "",
        }
      );
    }

    dialog.setField("instructores", nextRows);
  }

  function patchInstructorRow(instructorId, patch) {
    dialog.setField(
      "instructores",
      asArray(dialog.form.instructores).map((r) =>
        String(r.instructorId) === String(instructorId) ? { ...r, ...patch } : r
      )
    );
  }

  function setSelectedRemplazos(ids) {
    const idSet = new Set((ids || []).map(String));
    const prevByRemplazo = new Map(
      asArray(dialog.form.remplazos).map((r) => [String(r.remplazoId), r])
    );

    const nextRows = [];
    for (const remplazoId of idSet) {
      const prev = prevByRemplazo.get(remplazoId);
      nextRows.push(
        prev ?? {
          id: makePrefixedTempId(TMP_PREFIX),
          instructorId: "",
          remplazoId,
          calificacion: "",
        }
      );
    }

    dialog.setField("remplazos", nextRows);
  }

  function patchRemplazoRow(remplazoId, patch) {
    dialog.setField(
      "remplazos",
      asArray(dialog.form.remplazos).map((r) =>
        String(r.remplazoId) === String(remplazoId) ? { ...r, ...patch } : r
      )
    );
  }

  async function save() {
    setLocalError(null);

    const nextSocioIds = new Set(asArray(dialog.form.socioIds).map(String));
    const prevSocioIds = new Set(asistenciaSocios.map((x) => String(x.socioId || x.id)));

    const toAddSocios = Array.from(nextSocioIds).filter((id) => !prevSocioIds.has(id));
    const toRemoveSocios = Array.from(prevSocioIds).filter((id) => !nextSocioIds.has(id));

    const normalizedInstructores = asArray(dialog.form.instructores).map((r) => ({
      instructorId: String(r.instructorId),
      puntualidad: flagsToPuntualidad(!!r.llegoTarde, !!r.seFueTemprano),
      calificacion: toNullableInteger(r.calificacion),
    }));

    const prevInstructoresById = new Map(
      asistenciaInstructores.map((x) => [
        String(x.instructorId),
        { puntualidad: Number(x.puntualidad), calificacion: x.calificacion == null ? null : Number(x.calificacion) },
      ])
    );
    const nextInstructoresById = new Map(normalizedInstructores.map((x) => [x.instructorId, x]));

    const toRemoveInstructores = Array.from(prevInstructoresById.keys()).filter((id) => !nextInstructoresById.has(id));

    const normalizedRemplazos = asArray(dialog.form.remplazos).map((r) => ({
      id: String(r.id),
      instructorId: toNullableId(r.instructorId),
      remplazoId: String(r.remplazoId),
      calificacion: toNullableInteger(r.calificacion),
    }));

    const prevRemplazosById = new Map(
      asistenciaRemplazos.map((x) => [
        String(x.id),
        {
          instructorId: x.instructorId == null ? null : String(x.instructorId),
          remplazoId: String(x.remplazoId),
          calificacion: x.calificacion == null ? null : Number(x.calificacion),
        },
      ])
    );

    const nextPersistedIds = new Set(normalizedRemplazos.filter((x) => !isPrefixedTempId(x.id, TMP_PREFIX)).map((x) => x.id));
    const toDeleteRemplazos = Array.from(prevRemplazosById.keys()).filter((id) => !nextPersistedIds.has(id));
    const toCreateRemplazos = normalizedRemplazos.filter((x) => isPrefixedTempId(x.id, TMP_PREFIX) || !prevRemplazosById.has(x.id));
    const toPatchRemplazos = normalizedRemplazos.filter((x) => !isPrefixedTempId(x.id, TMP_PREFIX) && prevRemplazosById.has(x.id));

    try {
      for (const socioId of toRemoveSocios) await removeSocio.mutateAsync({ actividadId, socioId });
      for (const socioId of toAddSocios) await addSocio.mutateAsync({ actividadId, socioId });

      for (const instructorId of toRemoveInstructores) {
        await removeInstructor.mutateAsync({ actividadId, instructorId });
      }

      for (const row of normalizedInstructores) {
        const prev = prevInstructoresById.get(row.instructorId);
        if (!prev) {
          await addInstructor.mutateAsync({
            actividadId,
            instructorId: row.instructorId,
            puntualidad: row.puntualidad,
          });
          if (row.calificacion != null) {
            await patchInstructor.mutateAsync({
              actividadId,
              instructorId: row.instructorId,
              patch: { calificacion: row.calificacion },
            });
          }
          continue;
        }

        const patch = {};
        if (prev.puntualidad !== row.puntualidad) patch.puntualidad = row.puntualidad;
        if (prev.calificacion !== row.calificacion) patch.calificacion = row.calificacion;
        if (Object.keys(patch).length > 0) {
          await patchInstructor.mutateAsync({ actividadId, instructorId: row.instructorId, patch });
        }
      }

      for (const rowId of toDeleteRemplazos) {
        await deleteRemplazo.mutateAsync({ actividadId, remplazoRowId: rowId });
      }

      for (const row of toCreateRemplazos) {
        await createRemplazo.mutateAsync({
          actividadId,
          payload: {
            instructorId: row.instructorId,
            remplazoId: row.remplazoId,
            calificacion: row.calificacion,
          },
        });
      }

      for (const row of toPatchRemplazos) {
        const prev = prevRemplazosById.get(row.id);
        if (!prev) continue;

        const patch = {};
        if (prev.instructorId !== row.instructorId) patch.instructorId = row.instructorId;
        if (prev.remplazoId !== row.remplazoId) patch.remplazoId = row.remplazoId;
        if (prev.calificacion !== row.calificacion) patch.calificacion = row.calificacion;
        if (Object.keys(patch).length > 0) {
          await patchRemplazo.mutateAsync({ actividadId, remplazoRowId: row.id, patch });
        }
      }

      dialog.close();
    } catch (e) {
      setLocalError(String(e?.message || e));
      throw e;
    }
  }

  return (
    <>
      <DataCard title="Asistencia" variant="cyan" headerIcon={<MdPeople size="24px" />} onEdit={openDialog} editAriaLabel="Editar asistencia" isIncomplete={isIncomplete}>
        <Box divideY="1px" divideColor="dashboard.border.muted">
          <Box pb="4">
            <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em" mb="3">Socios</Text>
            <HStack gap="6">
              <HStack gap="2.5" align="start">
                <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdPeople size="16px" /></Box>
                <Stack gap="0">
                  <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Registrados</Text>
                  <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{sociosRegistrados}</Text>
                </Stack>
              </HStack>

              <HStack gap="2.5" align="start">
                <Box color="dashboard.fg.muted" mt="0.5" flexShrink={0}><MdPerson size="16px" /></Box>
                <Stack gap="0">
                  <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">No Registrados</Text>
                  <Text fontWeight="600" fontSize="sm" color="dashboard.fg.default">{sociosNoRegistrados}</Text>
                </Stack>
              </HStack>
            </HStack>
          </Box>

          <Box py="4">
            <HStack justify="space-between" mb="3">
              <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Instructores</Text>
              <Badge variant="subtle" size="sm">{asistenciaInstructores.length}</Badge>
            </HStack>

            {instructoresQ.isLoading ? (
              <Text color="dashboard.fg.muted" fontSize="sm">Cargando asistencia de instructores...</Text>
            ) : asistenciaInstructores.length === 0 ? (
              <Text color="dashboard.fg.muted" fontSize="sm">Sin asistencia de instructores registrada.</Text>
            ) : (
              <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
                {asistenciaInstructores.map((row) => {
                  const profile = instructoresById.get(String(row.instructorId));
                  const nombre = row.instructorNombre || profile?.nombre || "Instructor";
                  const avatarSrc = resolveAvatarUrl(row) || resolveAvatarUrl(profile);
                  const tone = rowTone(row.instructorId || nombre);
                  return (
                    <Box key={row.instructorId} rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" overflow="hidden">
                      <HStack gap="2.5" align="center" minW="0">
                        <AppAvatar name={nombre} src={avatarSrc} seed={row.instructorId || nombre} />
                        <Stack gap="1" justify="center" minW="0" flex="1">
                          <HStack gap="2" minW="0" justify="space-between">
                            <DashboardTruncatedText fontWeight="800" fontSize="sm" color="dashboard.fg.default">
                              {nombre}
                            </DashboardTruncatedText>
                            <PunctualityAndRating puntualidad={row.puntualidad} calificacion={row.calificacion} />
                          </HStack>
                        </Stack>
                      </HStack>
                    </Box>
                  );
                })}
              </Grid>
            )}
          </Box>

          {remplazosQ.isLoading || asistenciaRemplazos.length > 0 ? (
            <Box pt="4">
              <HStack justify="space-between" mb="3">
                <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Remplazos</Text>
                <Badge variant="subtle" size="sm">{asistenciaRemplazos.length}</Badge>
              </HStack>

              {remplazosQ.isLoading ? (
                <Text color="dashboard.fg.muted" fontSize="sm">Cargando remplazos...</Text>
              ) : (
                <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
                  {asistenciaRemplazos.map((row) => {
                    const profile = remplazosById.get(String(row.remplazoId));
                    const nombre = row.remplazoNombre || profile?.nombre || "Instructor remplazo";
                    const avatarSrc = resolveAvatarUrl(row) || resolveAvatarUrl(profile);
                    const tone = rowTone(row.remplazoId || nombre);
                    return (
                      <Box key={row.id} rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" overflow="hidden">
                        <HStack gap="2.5" align="center" minW="0">
                          <AppAvatar name={nombre} src={avatarSrc} seed={row.remplazoId || nombre} />
                          <Stack gap="1" justify="center" minW="0" flex="1">
                            <HStack gap="2" minW="0" justify="space-between">
                              <DashboardTruncatedText fontWeight="800" fontSize="sm" color="dashboard.fg.default">
                                {nombre}
                              </DashboardTruncatedText>
                              <PunctualityAndRating puntualidad={row.puntualidad} calificacion={row.calificacion} />
                            </HStack>
                            {row.instructorNombre ? (
                              <DashboardTruncatedText color="dashboard.fg.muted" fontSize="xs" fontWeight="700">
                                {row.instructorNombre}
                              </DashboardTruncatedText>
                            ) : null}
                          </Stack>
                        </HStack>
                      </Box>
                    );
                  })}
                </Grid>
              )}
            </Box>
          ) : null}
        </Box>
      </DataCard>

      <FormDialog
        open={dialog.open}
        onOpenChange={dialog.onOpenChange}
        title="Editar Asistencia"
        errorText={errorText}
        saving={saving}
        onCancel={dialog.close}
        onSave={save}
      >
        <Box>
          <TagCombobox
            label="Socios registrados en asistencia"
            placeholder="Agregar socios..."
            items={sociosItems}
            value={asArray(dialog.form.socioIds)}
            onChange={(next) => dialog.setField("socioIds", next)}
            allowCustomValue={false}
          />
        </Box>

        <Box>
          <TagCombobox
            label="Instructores de la actividad"
            placeholder="Agregar instructores..."
            items={instructoresItems}
            value={asArray(dialog.form.instructores).map((r) => String(r.instructorId))}
            onChange={setSelectedInstructores}
            allowCustomValue={false}
            showAvatar
          />

          {asArray(dialog.form.instructores).length > 0 ? (
            <Stack gap="2" mt="3">
              <HeaderRow c1="Instructor" c2="Puntualidad" c3="Calificación" />
              {asArray(dialog.form.instructores).map((row) => {
                const nombre = nombreInstructorById.get(String(row.instructorId)) || "Instructor";
                const profile = instructoresById.get(String(row.instructorId));
                const avatarSrc = resolveAvatarUrl(profile);
                return (
                  <Grid key={row.instructorId} templateColumns="2fr 2fr 1.5fr" gap="3" alignItems="center" px="2" py="2">
                    <HStack>
                      <AppAvatar size="sm" name={nombre} src={avatarSrc} seed={row.instructorId || nombre} />
                      <Text>{nombre}</Text>
                    </HStack>

                    <HStack gap="2" justify="center">
                      <Tooltip.Root openDelay={0}>
                        <CheckboxCard.Root
                          unstyled
                          checked={!!row.llegoTarde}
                          onCheckedChange={(e) => patchInstructorRow(row.instructorId, { llegoTarde: !!e.checked })}
                        >
                          <CheckboxCard.HiddenInput />
                          <Tooltip.Trigger asChild>
                            <DashboardCheckboxCardControl p="2.5" rounded="pill" accentColor="orange.400" boxSize="10">
                              <Icon color="orange.400" fontSize="xl">
                                <MdAccessTime />
                              </Icon>
                            </DashboardCheckboxCardControl>
                          </Tooltip.Trigger>
                        </CheckboxCard.Root>
                        <Tooltip.Positioner>
                          <Tooltip.Content>Llego Tarde</Tooltip.Content>
                        </Tooltip.Positioner>
                      </Tooltip.Root>

                      <Tooltip.Root openDelay={0}>
                        <CheckboxCard.Root
                          unstyled
                          checked={!!row.seFueTemprano}
                          onCheckedChange={(e) => patchInstructorRow(row.instructorId, { seFueTemprano: !!e.checked })}
                        >
                          <CheckboxCard.HiddenInput />
                          <Tooltip.Trigger asChild>
                            <DashboardCheckboxCardControl p="2.5" rounded="pill" accentColor="blue.400" boxSize="10">
                              <Icon color="blue.400" fontSize="xl">
                                <MdLogout />
                              </Icon>
                            </DashboardCheckboxCardControl>
                          </Tooltip.Trigger>
                        </CheckboxCard.Root>
                        <Tooltip.Positioner>
                          <Tooltip.Content>Se fue Temprano</Tooltip.Content>
                        </Tooltip.Positioner>
                      </Tooltip.Root>
                    </HStack>

                    <HStack justify="center">
                      <CompactEmojiRatingPicker
                        value={String(row.calificacion || "")}
                        onChange={(v) => patchInstructorRow(row.instructorId, { calificacion: v })}
                        isDisabled={saving}
                        ariaLabel={`Calificacion de ${nombre}`}
                      />
                    </HStack>
                  </Grid>
                );
              })}
            </Stack>
          ) : null}
        </Box>

        <Box>
          <TagCombobox
            label="Remplazos"
            placeholder="Agregar remplazos..."
            items={remplazosItems}
            value={asArray(dialog.form.remplazos).map((r) => String(r.remplazoId))}
            onChange={setSelectedRemplazos}
            allowCustomValue={false}
            showAvatar
          />

          {asArray(dialog.form.remplazos).length > 0 ? (
            <Stack gap="2" mt="3">
              <HeaderRow c1="Instructor del remplazo" c2="Instructor del grado" c3="Calificación" />
              {asArray(dialog.form.remplazos).map((row) => {
                const nombre = nombreInstructorById.get(String(row.remplazoId)) || "Instructor";
                const profile = remplazosById.get(String(row.remplazoId));
                const avatarSrc = resolveAvatarUrl(profile);
                return (
                  <Grid key={row.remplazoId} templateColumns="2fr 2fr 1.5fr" gap="3" alignItems="center" px="2" py="2">
                    <HStack>
                      <AppAvatar size="sm" name={nombre} src={avatarSrc} seed={row.remplazoId || nombre} />
                      <Text>{nombre}</Text>
                    </HStack>

                    <AvatarSelectField
                      collection={solicitanteCollection}
                      value={row.instructorId || ""}
                      onChange={(v) => patchRemplazoRow(row.remplazoId, { instructorId: v })}
                      placeholder="Solicitante (opcional)"
                    />

                    <HStack justify="center">
                      <CompactEmojiRatingPicker
                        value={String(row.calificacion || "")}
                        onChange={(v) => patchRemplazoRow(row.remplazoId, { calificacion: v })}
                        isDisabled={saving}
                        ariaLabel={`Calificacion de ${nombre}`}
                      />
                    </HStack>
                  </Grid>
                );
              })}
            </Stack>
          ) : null}
        </Box>
      </FormDialog>
    </>
  );
};

